<template>
  <div @mouseenter="onElementMouseEnter" class="step-element">
    <p class="step-element_title">
      <span class="pr-1">{{ index + 1 }}. {{ element.elementName }}:</span>

      <v-tooltip
        v-if="element.tooltip"
        right
        transition=" "
        content-class="step-element_tooltip"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-icon color="#000000" dark v-bind="attrs" v-on="on">
            mdi-information-outline
          </v-icon>
        </template>

        <span>{{ element.tooltip }}</span>
      </v-tooltip>

      <button
        v-if="isAdditionalElement"
        @click="onRemoveElementBtnClick"
        class="step-element_remove-btn step-element_remove-btn--element"
      >
        <v-icon x-small color="#000000">mdi-close</v-icon>
      </button>
    </p>

    <table v-if="element.range" class="step-element_table">
      <tr>
        <td class="step-element_table_fist-col">
          {{ mess["mill_lining_individual_solutions_input_rows_qty_label"] }}
        </td>

        <td>
          <v-text-field
            v-model.number="element.range_value"
            @input="validateInput($event, 'range_value', 'range')"
            :key="rerenderKey"
            ref="range"
            min="0"
            step="1"
            hide-details
            dense
            outlined
            type="number"
          />
        </td>

        <td></td>
        <td></td>
        <td></td>
      </tr>
    </table>

    <table v-if="defaultTypes.includes(element.type)" class="step-element_table">
      <tr>
        <th v-for="(header, i) in headers" :key="`header-${i}`">
          <template v-if="rowTemplate.hasOwnProperty(header.value)">
            {{ header.text }}
          </template>
        </th>
      </tr>

      <tr v-for="(row, i) in element.rows" :key="`row-${i}`">
        <td class="step-element_table_fist-col">
          <span v-if="element.type === 'table_multiple'">
            {{ row.element }} {{ i + 1 }}
          </span>
        </td>

        <td>
          <template v-if="element.type === 'table_multiple'">
            <span
              v-if="isEdited === -1 || isEdited !== i"
              @click="isEdited = i"
              @keyup.enter="isEdited = i"
              class="step-element_name-text"
              tabindex="0"
            >
              {{ row.name }}
            </span>

            <v-text-field
              v-if="isEdited === i"
              v-model="row.name"
              @blur="isEdited = -1"
              @keyup.enter.esc="isEdited = -1"
              :autofocus="isEdited === i"
              hide-details
              dense
              outlined
              type="text"
            />
          </template>
        </td>

        <td>
          <v-select
            v-model="row.material"
            :items="materials"
            item-text="name"
            item-value="name"
            :menu-props="{ maxHeight: '400' }"
            :label="
              mess['mill_lining_individual_solutions_input_material_label']
            "
            dense
            outlined
            hide-details
            class="step-element_select"
          >
            <template v-slot:item="{ item }">
              <v-tooltip
                v-if="materialsDescription[item.name]"
                right
                transition=" "
                content-class="step-element_tooltip"
              >
                <template v-slot:activator="{ on, attrs }">
                  <div
                    dark
                    v-bind="attrs"
                    v-on="on"
                    class="v-list-item__title"
                    style="display: flex;
                           align-items: center;
                           width: 100%;
                           min-height: 40px"
                  >
                    {{ item.name }}
                  </div>
                </template>

                <span v-html="materialsDescription[item.name]"></span>
              </v-tooltip>

              <span v-else class="v-list-item__title">{{ item.name }}</span>
            </template>
          </v-select>
        </td>
        <td>
          <v-text-field
            v-model.number="row.weight"
            @input="validateInput($event, `rows[${i}].weight`, `weight[${i}]`)"
            :value="row.weight"
            :ref="`weight`"
            :key="rerenderKey"
            hide-details
            dense
            outlined
            type="number"
          />
        </td>

        <td>
          <template v-if="element.type === 'table_multiple'">
            <button
              v-if="i > element.rows_default_qty - 1"
              @click="onRemoveRowBtnClick($event, i)"
              class="step-element_remove-btn"
            >
              <v-icon x-small color="#000000">mdi-close</v-icon>
            </button>
          </template>

          <v-text-field
            v-model.number="row.qty"
            @input="validateInput($event, `rows[${i}].qty`, `qty[${i}]`)"
            :value="row.qty"
            :ref="`qty`"
            :key="rerenderKey"
            hide-details
            dense
            outlined
            type="number"
          />
        </td>
      </tr>
    </table>

    <v-select
      v-else
      v-model="element.rows[0].basis"
      :items="bases"
      item-text="title"
      item-value="id"
      :menu-props="{ maxHeight: '400' }"
      :label="mess['mill_lining_individual_solutions_input_material_label']"
      dense
      outlined
      hide-details
      class="step-element_select mt-4"
    />

    <div class="d-flex align-center">
      <v-btn
        v-if="isRowAddable"
        @click="onAddRowBtnClick"
        class="step-element_btn-add-row"
        text
      >
        + {{ mess["mill_lining_individual_solutions_add"] }} {{ mess["mill_lining_individual_solutions_element"] }}
      </v-btn>

      <span v-if="!isValid" class="step-element_error-message">
        {{ mess["mill_lining_individual_solutions_element_error_msg"] }}
      </span>
    </div>
  </div>
</template>

<script>
import set from "lodash/set";
import get from "lodash/get";
import deepFreeze from "@/utils/deep-freeze";

export default {
  name: "StepElement",
  props: {
    element: {
      type: Object,
      default: () => ({})
    },
    headers: {
      type: Array,
      default: () => []
    },
    bases: {
      type: Array,
      default: () => []
    },
    materials: {
      type: Array,
      default: () => []
    },
    index: {
      type: [Number, null],
      default: null
    },
    isAdditionalElement: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      defaultTypes: ["table_single", "table_multiple"],
      rowTemplate:
        deepFreeze(
          Object.assign({}, { ...this.element.rows[0], weight: 0, qty: 0 })
        ) || Object.freeze({}),
      isEdited: -1,
      range: 0,
      rerenderKey: Date.now()
    };
  },
  methods: {
    onElementMouseEnter() {
      this.$emit("element-mouseover", this.element);
    },
    onRemoveElementBtnClick() {
      this.$emit("element-remove", this.element);
    },
    onRemoveRowBtnClick($event, i) {
      this.element.rows = this.element.rows.filter((r, _i) => _i !== i);
    },
    onAddRowBtnClick() {
      this.element.rows.push(Object.assign({}, this.rowTemplate));
    },
    validateInput($event, path, ref) {
      if (!$event || isNaN($event) || $event < 0) {
        set(this.element, path, 0);
        this.rerenderKey = Date.now();
        this.$nextTick(() => {
          get(this.$refs, ref)
            .$el.querySelector("input")
            .focus();
        });
      }
    }
  },
  computed: {
    isRowAddable() {
      return this.element.rows_max_qty > 0
        ? this.element.rowElementAddable &&
            this.element.rows.length < this.element.rows_max_qty
        : this.element.rowElementAddable;
    },
    isValid() {
      if (!this.element.range) return true;
      return (
        this.element.rows.reduce((acc, row) => acc + row.qty, 0) ===
        this.element.range_value
      );
    },
    materialsDescription() {
      return this.materials.reduce(
        (acc, m) => ({ ...acc, [m.name]: m.description }),
        {}
      );
    }
  }
};
</script>

<style lang="scss">
@import "StepElement";
</style>
